import { Inject, Injectable } from "@angular/core";
import { Observable, combineLatest, of } from "rxjs";
import { catchError, map, mergeMap } from 'rxjs/operators'
import { AirportEntity, AirportWithAddressInfoQueryEntity, AppMetaData, AppMetaDataItemNames, ApplicantPlaceholderEntity, CalendarReferenceEntity, CheckRideGroupAllItems, CheckRideOutcomeAllItems, CheckRideOwnershipTypeAllItems, CheckRideRequestCalendarQueryEntity, CheckRideRequestEntity, CheckRideRequestPaymentPartyAllItems, CheckRideRequestPaymentPartyEntity, CheckRideRequestSubStatusAllItems, CheckRideScenarioEntity, CheckRideTestTypeAllItems, CheckRideTypeAllItems, CheckRideWorkflowAllItems, PilotExaminerEntity, PilotExaminerLocationEntity, PilotExaminerSearchQueryEntity, PilotExaminerStatusAllItems, PilotExaminerTypeAllItems, ScheduleBlockEntity, ScheduleBlockItemEntity, ScheduleBlockItemStatusAllItems, ScheduleBlockItemTypeAllItems, ScheduleBlockSchoolBookingOptionAllItems, StudentApplicantEntity, UserTypeAllItems } from "../../../meta-data/app-meta-data.service";
import { DwMetaDataServiceToken, DwMetaDataService, DwOrmDataService, DwOrmDataServiceToken, DwQuery, deepCopy, DwConfigService, DwConfigServiceToken, DwLookupInfo, SuppressErrorMessagesHeader, DwSecurityUserService, DwQueryMetaData, DwCacheService, DwSecurityTopics, DwQueryFilter, DwFormContextMode, DwModalConfig, DwModalButtonStandardConfigs, DwModalSize, DwMetaDataRoutingService, DwMenuActionType, DwModalResult, DwRoutingService } from "@devwareapps/devware-cap";
import { ExaminerLocationSearchRequest } from "../../examiner/models/examiner-location-search-request.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { CheckrideReferenceInfo } from "../../examiner/models/checkride-reschedule-info.model";
import { AirportMetar } from "../../examiner/models/airport-metar.model";
import { WeatherBasicMetarDTO } from "../../examiner/models/weather-basic-metar.model";
import { CheckCalendarAvailabilityRequestDTO } from "../../examiner/models/check-calendar-availability-request.model";
import { CheckCalendarAvailabilityResultDTO } from "../../examiner/models/check-calendar-availability-result.model";
import { PilotExaminerSearchQueryEntityExt } from "../../../meta-data/extensions/pilot-examiner-search-query-ext.model";
import { ScheduleHoldRequest } from "../../examiner/models/schedule-hold-request.model";
import { ScheduleHoldResult } from "../../examiner/models/schedule-hold-result.model";
import { MetarRepositoryService } from "../../shared/services/metar-repository-service";
import { AdminManagerService } from "../../shared/services/admin-manager.service";
import { PilexosUserTypeIds } from "../../shared/models/pilexos-user-type.enum";
import { ExaminerBookingVariableContext } from "../../booking/models/examiner-booking-variable-context.model";
import { ScheduleBlocksRequest } from "../models/schedule-block-request.model";
import { ScheduleBlockCalendarEvent } from "../models/schedule-block-calendar-event.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { ScheduleBlockDateRef } from "../models/schedule-block-date-ref.model";
import { Router } from "@angular/router";
import { CalendarWeekDayEvent } from "../../calendar/models/calendar-weekday-event.model";
import { CalendarExtendedData } from "../../calendar/models/calendar-extended-data.model";
import { ScheduleBlockSchoolExaminerOptions } from "../models/schedule-block-school-examiner-options.model";
import { ExaminerRepositoryService } from "../../examiner/services/examiner-repository.service";
import { AirportValidationResult, ExaminerLocationValidationResult } from "../models/airport-validation-result.model";

@Injectable({
    providedIn: 'root'
})
export class ScheduleBlockService {

    apiRoot: string;
    static CurrentBlockDates: ScheduleBlockDateRef[];

    modalSize = {
        width: "600px",
        height: "650px"
    }

    constructor(
        private http: HttpClient,
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private metarRepositoryService: MetarRepositoryService,
        private adminManagerService: AdminManagerService,
        private dateTimeUtilService: DateTimeUtilService,
        private metaDataRoutingService: DwMetaDataRoutingService,
        private router: Router,
        private dwRoutingService: DwRoutingService,
        private examinerRepositoryService: ExaminerRepositoryService

    ) {
        this.apiRoot = configService.coreConfig.apiRoot;


        this.router.events.subscribe((event) => {
            ScheduleBlockService.CurrentBlockDates = null;
        });
    }

    setCurrentBlockDates(dates: ScheduleBlockDateRef[]) {
        ScheduleBlockService.CurrentBlockDates = dates;
    }

    getCurrentBlockDates(): ScheduleBlockDateRef[] {
        return ScheduleBlockService.CurrentBlockDates;
    }

    getScheduleBlocks(request: ScheduleBlocksRequest): Observable<ScheduleBlockCalendarEvent[]> {
        let url = `${this.apiRoot}/pilexos/checkride-blocks/search`;

        let headers = new HttpHeaders();

        headers = headers.append(SuppressErrorMessagesHeader, 'true');

        return this.http.post<ScheduleBlockCalendarEvent[]>(url, request, { headers: headers });
    }

    private isCheckInstructorUser(user: any): boolean {
        return user?.UserTypeId === PilexosUserTypeIds.checkInstructor;
    }

    private isSchoolAdminUser(user: any): boolean {
        return user?.UserTypeId === PilexosUserTypeIds.flightSchool;
    }

    private isFlightInstructorUser(user: any): boolean {
        return user?.UserTypeId === PilexosUserTypeIds.flightInstructor;
    }

    private isOwnSchoolExaminerBlock(scheduleBlock: ScheduleBlockEntity | ScheduleBlockCalendarEvent, user: any): boolean {
        return !!user?.flightSchoolExaminerId && scheduleBlock?.FlightSchoolExaminerId === user.flightSchoolExaminerId;
    }

    canEditBlock(scheduleBlock: ScheduleBlockEntity, schoolExaminerOptions: ScheduleBlockSchoolExaminerOptions[]): { canEdit: boolean, reason?: string } {
        const user = this.adminManagerService.getPilexosUser();

        const result: { canEdit: boolean, reason?: string } = { canEdit: true };

        if (this.isFlightInstructorUser(user)) {
            return { canEdit: false, reason: 'Flight Instructors do not have access to schedule blocks.' };
        }

        if (!scheduleBlock.PilotExaminerId) {
            return result;
        }

        if (this.isCheckInstructorUser(user) && this.isOwnSchoolExaminerBlock(scheduleBlock, user)) {
            return result;
        }

        // Limit edit access for school users
        if (this.isSchoolAdminUser(user)) {
            let examinerOptions: ScheduleBlockSchoolExaminerOptions; // = {}; ScheduleBlockSchoolDefaultOptionId: ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyCanView} as any;

            if (scheduleBlock.PilotExaminerId) {
                examinerOptions = schoolExaminerOptions?.find(o => o.PilotExaminerId === scheduleBlock.PilotExaminerId);
            }

            switch (examinerOptions?.ScheduleBlockSchoolDefaultOptionId) {
                case ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyCanView:
                    result.canEdit = false;
                    result.reason = 'Examiner has set blocks to view only, new blocks cannot be added.';
                    break;
                case ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyAllowSchoolBlocks:
                    result.canEdit = scheduleBlock.OwnershipTypeId === CheckRideOwnershipTypeAllItems.FlightSchool;

                    if (!result.canEdit) {
                        result.reason = 'Examiner has set blocks to view only, but school blocks can be added';
                    }
                    break;
                case ScheduleBlockSchoolBookingOptionAllItems.FullAccess:
                    result.canEdit = true;
                    break;
                default:
                    result.canEdit = false;
                    result.reason = 'Examiner has restricted access to this block.';
            }


        }

        return result;
    }

    canAddBlock(scheduleBlock: ScheduleBlockEntity, schoolExaminerOptions: ScheduleBlockSchoolExaminerOptions[]): { canAdd: boolean, reason?: string } {
        const user = this.adminManagerService.getPilexosUser();

        const result: { canAdd: boolean, reason?: string } = { canAdd: true };

        if (this.isFlightInstructorUser(user)) {
            return { canAdd: false, reason: 'Flight Instructors do not have access to schedule blocks.' };
        }

        if (!scheduleBlock.PilotExaminerId) {
            return result;
        }

        if (this.isCheckInstructorUser(user) && this.isOwnSchoolExaminerBlock(scheduleBlock, user)) {
            return result;
        }

        // Limit add access for school admin users
        if (this.isSchoolAdminUser(user)) {
            let examinerOptions: ScheduleBlockSchoolExaminerOptions; // = {}; ScheduleBlockSchoolDefaultOptionId: ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyCanView} as any;

            examinerOptions = schoolExaminerOptions?.find(o => o.PilotExaminerId === scheduleBlock.PilotExaminerId);

            switch (examinerOptions?.ScheduleBlockSchoolDefaultOptionId) {
                case ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyCanView:
                    result.canAdd = false;
                    result.reason = 'Examiner has set blocks to view only, new blocks cannot be added.';
                    break;
                case ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyAllowSchoolBlocks:
                case ScheduleBlockSchoolBookingOptionAllItems.FullAccess:
                    result.canAdd = true;
                    break;
                default:
                    result.canAdd = false;
                    result.reason = 'Examiner does not allow blocks to be added.';
            }
        }

        return result;
    }

    validateExaminerLocations(scheduleBlock: ScheduleBlockEntity, airportDetails: AirportWithAddressInfoQueryEntity[], schoolExaminerOptions: ScheduleBlockSchoolExaminerOptions[]): Observable<ExaminerLocationValidationResult> {
        const result: ExaminerLocationValidationResult = { isValid: true, hasWarnings: false, airportValiadtionResults: [] };

        if (!scheduleBlock.PilotExaminerId) {
            return of(result);
        }

        if (airportDetails.length == 0) {
            return of(result);
        }


        const user = this.adminManagerService.getPilexosUser();

        
        if (this.isSchoolAdminUser(user)) {
            let examinerOptions: ScheduleBlockSchoolExaminerOptions; // = {}; ScheduleBlockSchoolDefaultOptionId: ScheduleBlockSchoolBookingOptionAllItems.ReadOnlyCanView} as any;

            examinerOptions = schoolExaminerOptions?.find(o => o.PilotExaminerId === scheduleBlock.PilotExaminerId);

            // Only validate for Pilexos Examiners
            if (examinerOptions?.PilotExaminerTypeId != PilotExaminerTypeAllItems.PilExOSExaminer) {
                return of(result);
            }
        }

        return this.examinerRepositoryService.getExaminerLocationDistances(scheduleBlock.PilotExaminerId, airportDetails.map(a => a.AirportId))
            .pipe(map(examinerLocations => {

                for (let airportDetail of airportDetails) {
                    const airportResult: AirportValidationResult = { airport: airportDetail, isValid: true, showWarning: false };

                    result.airportValiadtionResults.push(airportResult);

                    let location = examinerLocations.find(loc => loc.AirportId == airportDetail.AirportId && (loc.WithinTravelRadius || loc.Distance == 0));

                    // If the location is found
                    if (location) {
                        if (location.Distance > 100) {
                            airportResult.showWarning = true;
                            airportResult.reason = `Airport ${airportDetail.AirportCode} is ${Math.round(location.Distance)} miles, but within travel radius of ${location.TravelRadiusMile} mile${location.TravelRadiusMile > 1 ? 's' : ''}`;
                        }
                        continue;
                    }

                    // Check if it's within the travel radious of a location
                    location = examinerLocations.find(loc => loc.ReferenceAirportId == airportDetail.AirportId && (loc.WithinTravelRadius || loc.Distance == 0));

                    if (location) {
                        if (location.Distance > 100) {
                            airportResult.showWarning = true;
                            airportResult.reason = `Airport ${airportDetail.AirportCode} is ${Math.round(location.Distance)} miles, but within travel radius of ${location.TravelRadiusMile} mile${location.TravelRadiusMile > 1 ? 's' : ''} from home airport ${location.AirportCode} - ${location.AirportName}`;
                        }
                        continue;
                    }

                    location = examinerLocations.find(loc => loc.ReferenceAirportId == airportDetail.AirportId);

                    if (location) {
                        airportResult.isValid = false;
                        airportResult.showWarning = false;
                        airportResult.reason = `Airport ${airportDetail.AirportCode} is outside Examiner travel radius.  Closest airport is ${location.AirportCode} - ${location.AirportName} which is ${Math.round(location.Distance)} mile${location.Distance > 1 ? 's' : ''}`;
                    } else {

                        airportResult.isValid = false;
                        airportResult.reason = `Airport ${airportDetail.AirportCode} is not setup in Examiner Locations`;
                        continue;
                    }
                }

                result.isValid = !result.airportValiadtionResults.find(r => !r.isValid);
                result.hasWarnings = !!result.airportValiadtionResults.find(r => r.showWarning);

                return result;
            }));
    }


    getSchoolExaminerOptions(): Observable<ScheduleBlockSchoolExaminerOptions[]> {
        const cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        return this.dwCacheService.get('ScheduleBlockSchoolExaminerOptions', this.loadSchoolExaminerOptions(), null, cacheInvalidationTopics);
    }

    private loadSchoolExaminerOptions(): Observable<ScheduleBlockSchoolExaminerOptions[]> {
        let url = `${this.apiRoot}/pilexos/checkride-blocks/school-examiner-options`;

        let headers = new HttpHeaders();

        headers = headers.append(SuppressErrorMessagesHeader, 'true');

        return this.http.get<ScheduleBlockSchoolExaminerOptions[]>(url, { headers: headers });
    }

    moveScheduleBlockItem(ScheduleBlockItemId: number, start: any, end: any) {
        return this.getScheduleBlockItem(ScheduleBlockItemId)
            .pipe(mergeMap(scheduleBlockItem => {
                this.setupScheduleBlockItemDateTimes(scheduleBlockItem, start, end);

                return this.dwOrmDataService.saveEntity(scheduleBlockItem);
            }));
    }

    private setupScheduleBlockItemDateTimes(scheduleBlockItem: ScheduleBlockItemEntity, start: any, end: any) {
        const dateRef = this.parseBlockStartEndDates(start, end);

        scheduleBlockItem.StartDate = dateRef.startDate;
        scheduleBlockItem.EndDate = dateRef.endDate;

        scheduleBlockItem.StartTime = dateRef.startTime;
        scheduleBlockItem.EndTime = dateRef.endTime;
    }

    private getScheduleBlockItem(ScheduleBlockItemId: number): Observable<ScheduleBlockItemEntity> {
        const query = AppMetaData.ScheduleBlockItem.CreateQueryBuilder();

        query.addFilterAnd(filters => filters.Equal(field => field.ScheduleBlockItemId, ScheduleBlockItemId));

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    parseBlockStartEndDates(start: any, end: any): ScheduleBlockDateRef {

        if (!start || !end) {
            return null;
        }

        const startDateTime = this.dateTimeUtilService.formatDateTime(start, 'MM/dd hh:mm a');
        const endTime = this.dateTimeUtilService.formatDateTime(end, 'hh:mm a');

        const dateRef: ScheduleBlockDateRef = {
            startDate: this.dateTimeUtilService.formatDateTimeToLocal(start, 'MM-dd-yyyy'),
            endDate: this.dateTimeUtilService.formatDateTimeToLocal(end, 'MM-dd-yyyy'),
            startTime: this.dateTimeUtilService.formatDateTime(start, 'HH:mm:ss'),
            endTime: this.dateTimeUtilService.formatDateTime(end, 'HH:mm:ss'),
            description: `${startDateTime} - ${endTime}`,
            fullStartDateTime: start,
            fullEndDateTime: end
        }

        return dateRef;
    }

    showAddBlockModal(startDate: string, endDate: string, ownershipTypeId: number): Observable<DwModalResult<any>> {
        const isSchool = ownershipTypeId === CheckRideOwnershipTypeAllItems.FlightSchool;

        const addContext = AppMetaData.ScheduleBlockItem.CreateEntity();

        this.setCurrentBlockDates([]);

        this.setupScheduleBlockItemDateTimes(addContext, startDate, endDate);

        let configKey = 'add';

        const modalOptions: Partial<DwModalConfig> = {
            title: 'Add Checkride Block',
        };

        if (isSchool) {
            configKey = 'add-school';
            modalOptions.title = 'Add School Checkride Block';
        }

        modalOptions.buttons = DwModalButtonStandardConfigs.instance.SaveAndClose;
        //modalOptions.buttons[0].buttonText = "Send";
        modalOptions.allowFullScreen = false;
        modalOptions.modalSize = DwModalSize.custom;
        modalOptions.customModalWidth = this.modalSize.width;
        modalOptions.customModalHeight = this.modalSize.height;

        return this.metaDataRoutingService.openAddEditViewModal(DwMenuActionType.add, AppMetaDataItemNames.ScheduleBlock, null, addContext, null, configKey, modalOptions, true)
            .pipe(map(result => {
                if (!result.cancelled) {

                }
                return result;
            }));
    }

    showAddBlockMultipleModal(currentBlockDates: ScheduleBlockDateRef[], ownershipTypeId: number): Observable<DwModalResult<ScheduleBlockEntity>> {
        const isSchool = ownershipTypeId === CheckRideOwnershipTypeAllItems.FlightSchool;

        this.setCurrentBlockDates(currentBlockDates);

        let configKey = 'add';

        const modalOptions: Partial<DwModalConfig> = {
            title: 'Add Checkride Block',
        };


        if (isSchool) {
            configKey = 'school-blocks-add';
            modalOptions.title = 'Add School Checkride Block';
        }

        modalOptions.buttons = DwModalButtonStandardConfigs.instance.SaveAndClose;
        //modalOptions.buttons[0].buttonText = "Send";
        modalOptions.allowFullScreen = false;
        modalOptions.modalSize = DwModalSize.custom;
        modalOptions.customModalWidth = this.modalSize.width;
        modalOptions.customModalHeight = this.modalSize.height

        return this.metaDataRoutingService.openAddEditViewModal(DwMenuActionType.add, AppMetaDataItemNames.ScheduleBlock, null, null, null, configKey, modalOptions, true)
            .pipe(map(result => {
                if (!result.cancelled) {

                }
                return result;
            }));
    }

    showEditBlockModal(scheduleBlockEvent: ScheduleBlockCalendarEvent, ownershipTypeId: number): Observable<DwModalResult<any>> {
        // const addContext = this.setupScheduleBlockItemDateTimes(AppMetaData.ScheduleBlockItem.CreateEntity(), startDate, endDate);
        const scheduleBlockItemId = scheduleBlockEvent.ScheduleBlockItemId;

        const isSchool = ownershipTypeId === CheckRideOwnershipTypeAllItems.FlightSchool;

        const modalActionType = scheduleBlockEvent.CanEdit ? DwMenuActionType.edit : DwMenuActionType.view;
        let configKey = ''; // Edit uses default, but not sure I love that since we now have 2

        const modalOptions: Partial<DwModalConfig> = {
            title: 'Edit Checkride Block',
        };

        if (isSchool) {
            configKey = 'school-blocks';
            modalOptions.title = 'Edit School Checkride Block';
        }

        if (scheduleBlockEvent.ScheduleBlockItemCount > 1) {
            modalOptions.title += ` (1 of ${scheduleBlockEvent.ScheduleBlockItemCount})`;
        }

        const deactivateButton = DwModalButtonStandardConfigs.instance.cancelButton

        deactivateButton.buttonText = 'Deactivate';
        deactivateButton.buttonClass = 'btn btn-warning';

        const editRelatedButton = DwModalButtonStandardConfigs.instance.cancelButton;
        editRelatedButton.buttonText = `Edit Related Blocks (${scheduleBlockEvent.ScheduleBlockItemCount})`;
        editRelatedButton.buttonClass = 'btn btn-warning';

        if (scheduleBlockEvent.CanEdit) {


            const customButtons = [];

            customButtons.push(deactivateButton);

            if (scheduleBlockEvent.ScheduleBlockItemCount > 1) {
                customButtons.push(editRelatedButton);
            }

            modalOptions.buttons = [DwModalButtonStandardConfigs.instance.saveButton, ...customButtons, DwModalButtonStandardConfigs.instance.cancelButton]
        }
        else {
            modalOptions.title = 'View School Checkride Block';
            modalOptions.buttons = [DwModalButtonStandardConfigs.instance.closeButton];
        }

        modalOptions.allowFullScreen = false;
        modalOptions.modalSize = DwModalSize.custom;
        modalOptions.customModalWidth = this.modalSize.width;
        modalOptions.customModalHeight = this.modalSize.height

        return this.metaDataRoutingService.openAddEditViewModal(modalActionType, AppMetaDataItemNames.ScheduleBlockItem, scheduleBlockItemId, null, null, configKey, modalOptions, true)
            .pipe(mergeMap(result => {
                if (result.cancelled && result.buttonConfig?.buttonText === deactivateButton.buttonText) {
                    return this.deleteScheduleBlockItem(scheduleBlockItemId).pipe(map(() => {
                        result.cancelled = false;

                        return result;
                    }));
                }
                if (result.cancelled && result.buttonConfig?.buttonText === editRelatedButton.buttonText) {
                    let url = '/settings/checkride-blocks/edit/' + scheduleBlockEvent.ScheduleBlockId;

                    if (isSchool) {
                        url = '/school-settings/checkride-blocks/edit/' + scheduleBlockEvent.ScheduleBlockId;
                    }

                    this.dwRoutingService.navigateToUrl(url);
                }

                return of(result);
            }));
    }

    deleteScheduleBlockItem(scheduleBlockItemId: number): Observable<any> {
        const scheduleBlockItem = AppMetaData.ScheduleBlockItem.CreateEntity();
        scheduleBlockItem.ScheduleBlockItemId = scheduleBlockItemId;
        scheduleBlockItem.ScheduleBlockItemStatusId = ScheduleBlockItemStatusAllItems.Inactive;

        return this.dwOrmDataService.saveEntity(scheduleBlockItem);
    }

    minBlockDurationMinutes = 60;

    private getEventStart(event: CalendarWeekDayEvent): string {
        const data: CalendarExtendedData = event.data;

        return data?.startDateTime;
    }

    private getEventEnd(event: CalendarWeekDayEvent): string {
        const data: CalendarExtendedData = event.data;

        return data?.endDateTime;
    }

    public adjustScheduleBlock(
        scheduleBlockEvent: CalendarWeekDayEvent,
        allEventsOriginal: CalendarWeekDayEvent[]
    ): CalendarWeekDayEvent[] {

        allEventsOriginal = allEventsOriginal.filter(e => this.isCheckrideEvent(e));

        const allEvents: { start: string; end: string }[] = allEventsOriginal.map(e => ({
            start: this.getEventStart(e),
            end: this.getEventEnd(e)
        }));

        const blockStart = this.getEventStart(scheduleBlockEvent);
        const blockEnd = this.getEventEnd(scheduleBlockEvent);

        // Helper: true overlap check (not just "within")
        const overlaps = (aStart: string, aEnd: string, bStart: string, bEnd: string) => {
            // Treat as [start, end) to avoid splitting on touching boundaries
            return this.dateTimeUtilService.isBefore(aStart, bEnd) &&
                this.dateTimeUtilService.isBefore(bStart, aEnd);
        };

        // 1) Find events that overlap the block (exclude the block itself if it's in allEvents)
        const crossOverEvents = allEvents
            .filter(e => e !== scheduleBlockEvent)
            .filter(e => overlaps(e.start, e.end, blockStart, blockEnd))
            // 2) Clip each event to the block bounds so we only subtract what's inside the block
            .map(e => ({
                ...e,
                start: this.dateTimeUtilService.isBefore(e.start, blockStart) ? blockStart : e.start,
                end: this.dateTimeUtilService.isBefore(blockEnd, e.end) ? blockEnd : e.end
            }))
            // 3) Sort by start time
            .sort((a, b) => this.dateTimeUtilService.compare(a.start, b.start)); // implement compare if you don't have it

        if (crossOverEvents.length === 0) return [scheduleBlockEvent];

        // 4) Merge overlapping/adjacent busy intervals
        const merged: Array<{ start: string; end: string }> = [];
        for (const e of crossOverEvents) {
            const last = merged[merged.length - 1];

            if (!last) {
                merged.push({ start: e.start, end: e.end });
                continue;
            }

            // If e starts before or exactly at last.end, merge them
            const startsBeforeOrAtLastEnd =
                !this.dateTimeUtilService.isBefore(last.end, e.start); // i.e. last.end >= e.start

            if (startsBeforeOrAtLastEnd) {
                // extend end = max(last.end, e.end)
                last.end = this.dateTimeUtilService.isBefore(last.end, e.end) ? e.end : last.end;
            } else {
                merged.push({ start: e.start, end: e.end });
            }
        }

        // 5) Subtract merged busy intervals from the block
        const finalEvents: CalendarWeekDayEvent[] = [];
        let currentStart = blockStart;

        for (const busy of merged) {
            // gap before busy?
            if (this.dateTimeUtilService.isBefore(currentStart, busy.start)) {
                const adjustedBlock = this.createNewBlock(scheduleBlockEvent, currentStart, busy.start);

                if (adjustedBlock) {
                    finalEvents.push(adjustedBlock);
                }
            }

            // advance currentStart to busy.end (max)
            currentStart = this.dateTimeUtilService.isBefore(currentStart, busy.end) ? busy.end : currentStart;

            // if we've reached/passed block end, we're done
            if (!this.dateTimeUtilService.isBefore(currentStart, blockEnd)) {
                return finalEvents;
            }
        }

        // tail after last busy?
        if (this.dateTimeUtilService.isBefore(currentStart, blockEnd)) {
            const adjustedBlock = this.createNewBlock(scheduleBlockEvent, currentStart, blockEnd);

            if (adjustedBlock) {
                finalEvents.push(adjustedBlock);
            }
            // finalEvents.push({
            //     ...scheduleBlockEvent,
            //     start: currentStart,
            //     end: blockEnd
            // });
        }

        return finalEvents;
    }

    private isCheckrideEvent(event: CalendarWeekDayEvent): boolean {
        const calendarData = (event.data as CalendarExtendedData);;

        return calendarData.type === 'checkride';
    }

    private createNewBlock(scheduleBlockEvent: CalendarWeekDayEvent, start: string, end: string): CalendarWeekDayEvent {
        const adjustedBlock: CalendarWeekDayEvent = {
            ...scheduleBlockEvent,
            start: start,
            end: end
        };

        const duration = this.dateTimeUtilService.calcDurationMinutes(start, end);

        if (duration < this.minBlockDurationMinutes) {
            return null;
        }

        const scheduleBlock = (scheduleBlockEvent.data as CalendarExtendedData)?.scheduleBlock;

        adjustedBlock.text += `\n\nPartial - Original ${scheduleBlock?.Description.substring(11) || ''}`;

        return adjustedBlock;
    }


}
